import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import pollService from "../../api/pollService";
import logoDark from "../../assets/images/logo-dark.svg";
import { useTheme } from "../../context/ThemeContext";
import "./PollVote.scss";

// Public poll-vote result landing page.
//
// Reached by tapping a one-tap "Vote: <option>" link inside a campaign email
// (single- or multi-select poll's per-option links). The link carries
// ?token=<signed token> — this page calls the backend itself (via the web
// app's own API client) to record the vote and get back the result, rather
// than the backend recording it and redirecting here directly. That's
// deliberate: it means only FRONTEND_URL needs to be a reachable address for
// vote links to work, since the backend is never linked to directly from an
// email — the browser reaches it the same way the rest of the web app does.
//
// (?status=... directly, with no token, is also still supported as a
// fallback for resilience, in case anything ever redirects here itself.)

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const STATUS_CONTENT = {
  success: (option) => ({
    badge: "is-success",
    icon: <CheckIcon />,
    title: "Thanks for voting!",
    subtitle: option
      ? (
        <>Your vote for <strong>{option}</strong> has been recorded.</>
      )
      : "Your vote has been recorded.",
  }),
  expired: () => ({
    badge: "is-warning",
    icon: <ClockIcon />,
    title: "This poll has expired",
    subtitle: "Voting has closed for this poll, so your response couldn't be recorded.",
  }),
  "already-voted": () => ({
    badge: "is-warning",
    icon: <ClockIcon />,
    title: "You've already voted",
    subtitle: "We already have a response from you on this poll — thanks for weighing in!",
  }),
  invalid: () => ({
    badge: "is-error",
    icon: <AlertIcon />,
    title: "This voting link isn't valid",
    subtitle: "The link may be old or malformed. Please use the latest email if you'd like to vote.",
  }),
  error: () => ({
    badge: "is-error",
    icon: <AlertIcon />,
    title: "Something went wrong",
    subtitle: "We couldn't record your vote just now. Please try the link again in a moment.",
  }),
};

const PollVoteResult = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(!!token);
  const [fetchedStatus, setFetchedStatus] = useState(null);
  const [fetchedOption, setFetchedOption] = useState("");

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    pollService
      .getPollByToken(token)
      .then((res) => {
        if (cancelled) return;
        const body = res?.data?.data || {};
        setFetchedStatus(body.status || "invalid");
        setFetchedOption(body.option || "");
      })
      .catch((err) => {
        console.error("Poll vote failed:", err?.response?.status, err?.response?.data || err?.message);
        if (!cancelled) setFetchedStatus("error");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    const loadingContent = {
      badge: "",
      icon: <ClockIcon />,
      title: "Recording your vote…",
      subtitle: "Just a moment.",
    };
    return <ResultCard content={loadingContent} />;
  }

  const status = token ? fetchedStatus : searchParams.get("status") || "error";
  const option = token ? fetchedOption : searchParams.get("option") || "";
  const content = (STATUS_CONTENT[status] || STATUS_CONTENT.error)(option);

  return <ResultCard content={content} />;
};

function ResultCard({ content }) {
  const { resolvedTheme } = useTheme();
  const logoSrc =
    resolvedTheme === "dark" ? logoDark : process.env.PUBLIC_URL + "/logo.svg";
  return (
    <div className="poll-vote-page">
      <div className="poll-vote-card">
        <div className="poll-vote-card__header">
          <img
            src={logoSrc}
            alt="MapMyPay"
            className="poll-vote-logo"
          />
          <div className={`poll-vote-badge ${content.badge}`}>{content.icon}</div>
        </div>

        <div className="poll-vote-card__body">
          <h1 className="poll-vote-title">{content.title}</h1>
          <p className="poll-vote-subtitle">{content.subtitle}</p>
          <a className="poll-vote-return" href={process.env.PUBLIC_URL + "/"}>
            Return to MapMyPay
          </a>
        </div>
      </div>
    </div>
  );
}

export default PollVoteResult;
